import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
  input,
} from '@angular/core';
import {
  AwBadgeComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  DialogFormControls,
  DialogOptions,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { GraphQLService } from '../../../core/services/graphql.service';
import {
  QueryableTable,
  QueryableTableAssignment,
} from '../../../api/models';
import { UnassignButtonComponent } from './unassign-button.component';

/**
 * Internal model for table rendering.
 */
export interface ApiAccessTableRow {
  tableId: string;
  tableName: string;
  assignedBadge: {
    isAssigned: boolean;
    color: string;
    label: string;
  };
}

/**
 * Pure function: merges queryable tables with assignments to produce table rows.
 */
export function buildTableRows(
  tables: QueryableTable[],
  assignments: QueryableTableAssignment[]
): ApiAccessTableRow[] {
  const assignedIds = new Set(
    assignments.map((a) => a.queryableTableId).filter((id): id is string => !!id)
  );
  return tables.map((table) => {
    const isAssigned = assignedIds.has(table.id ?? '');
    return {
      tableId: table.id ?? '',
      tableName: table.tableName ?? '',
      assignedBadge: {
        isAssigned,
        color: isAssigned ? 'success-green-700' : 'error-red',
        label: isAssigned ? 'Yes' : 'No',
      },
    };
  });
}

/**
 * Pure function: computes the union of existing and selected IDs (for Assign).
 */
export function computeAssignPayload(
  existingIds: string[],
  selectedIds: string[]
): string[] {
  return [...new Set([...existingIds, ...selectedIds])];
}

/**
 * Pure function: computes the difference of existing minus selected IDs (for Unassign).
 */
export function computeUnassignPayload(
  existingIds: string[],
  selectedIds: string[]
): string[] {
  const removeSet = new Set(selectedIds);
  return existingIds.filter((id) => !removeSet.has(id));
}

@Component({
  selector: 'app-api-access-section',
  standalone: true,
  imports: [
    CommonModule,
    AwTableComponent,
    AwSearchComponent,
    AwDialogComponent,
    AwDividerComponent,
    AwButtonDirective,
    AwSpinnerComponent,
    MatIcon,
    FormsModule,
    UnassignButtonComponent,
  ],
  templateUrl: './api-access-section.component.html',
  styleUrls: ['./api-access-section.component.scss'],
})
export class ApiAccessSectionComponent implements OnInit {
  @Input({ required: true }) organizationId!: string;
  @Input({ required: true }) authorizedParty!: string;
  @Input() showHeading = true;

  @Output() toastMessage = new EventEmitter<{
    message: string;
    variant: ToastVariants;
  }>();

  @ViewChild(AwTableComponent) awTableComponent!: AwTableComponent;

  isLoading = false;
  errorMessage: string | null = null;
  assignmentsExist = false;

  apiAccessTableData = signal<ApiAccessTableRow[]>([]);
  apiAccessTableDataCopy: ApiAccessTableRow[] = [];
  searchValue = '';
  checkedRows: TableRow[] = [];
  disableChangeAssignment = true;

  openChangeAssignmentDialog = false;

  currentAssignedTableIds: Set<string> = new Set();

  columnsDefinition: TableCellInput[] = [
    {
      type: TableCellTypes.Checkbox,
      key: 'checkbox',
      isExpanded: false,
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'tableName',
      label: 'Table Name',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Custom,
      key: 'assigned',
      label: 'Assigned',
      isExpanded: false,
      combineFields: ['assignedBadge.color', 'assignedBadge.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: { color: values[0], label: values[1] },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Custom,
      key: 'unassign',
      label: 'Unassign',
      isExpanded: false,
      combineFields: ['assignedBadge.isAssigned'],
      combineTemplate: (values: any[], rowData?: any) => ({
        component: UnassignButtonComponent,
        componentData: {
          isAssigned: values[0],
          rowData: rowData,
          onUnassign: (row: any) => this.unassignSingleTable(row),
        },
      }),
      align: 'center',
    },
  ];

  changeAssignmentFormFields = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'assignmentAction',
        label: 'Change Assignment',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Assign', value: 'assign' },
            { label: 'Unassign', value: 'unassign' },
          ],
        },
        validators: {
          required: { value: true, message: 'Please select an action' },
        },
      },
    ],
  });

  changeAssignmentDialogOptions = input<DialogOptions>({
    variant: 'standard',
    title: 'Change Assignment',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  });

  constructor(private graphQLService: GraphQLService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.graphQLService
      .getAllQueryableTables()
      .pipe(
        switchMap((tables) => {
          return this.graphQLService
            .getAssignments(this.organizationId, this.authorizedParty)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                  this.assignmentsExist = false;
                  return of([]);
                }
                throw error;
              }),
              switchMap((assignments) => {
                this.assignmentsExist = assignments.length > 0;
                this.currentAssignedTableIds = new Set(
                  assignments
                    .map((a) => a.queryableTableId)
                    .filter((id): id is string => !!id)
                );
                const rows = buildTableRows(tables, assignments);
                this.apiAccessTableData.set(rows);
                this.apiAccessTableDataCopy = rows;
                return of(null);
              })
            );
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.errorMessage =
              'The GraphQL API is not configured in this environment';
          } else {
            this.errorMessage =
              typeof error.error === 'string'
                ? error.error
                : 'Failed to fetch tables available for API querying';
          }
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  onSearchTable(searchInput: string): void {
    this.awTableComponent?.applyFilter(searchInput);
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }): void {
    this.checkedRows = checkedRows.rows;
    this.disableChangeAssignment = this.checkedRows.length === 0;
  }

  unassignSingleTable(row: any): void {
    const tableId = row['tableId'];
    if (!tableId || !this.currentAssignedTableIds.has(tableId)) {
      return;
    }

    const newIds = [...this.currentAssignedTableIds].filter(
      (id) => id !== tableId
    );

    this.isLoading = true;
    const operation$: Observable<void> =
      newIds.length === 0
        ? this.graphQLService.deleteAssignments(
            this.organizationId,
            this.authorizedParty
          )
        : this.graphQLService.updateAssignments(
            this.organizationId,
            this.authorizedParty,
            newIds
          ).pipe(map(() => void 0));

    operation$
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.currentAssignedTableIds.delete(tableId);
          if (newIds.length === 0) {
            this.assignmentsExist = false;
          }

          const updatedRows = this.apiAccessTableDataCopy.map((r) => {
            if (r.tableId === tableId) {
              return {
                ...r,
                assignedBadge: {
                  isAssigned: false,
                  color: 'error-red',
                  label: 'No',
                },
              };
            }
            return r;
          });
          this.apiAccessTableData.set(updatedRows);
          this.apiAccessTableDataCopy = updatedRows;
          this.toastMessage.emit({
            message: 'Successfully unassigned table',
            variant: 'success',
          });
        },
        error: () => {
          this.toastMessage.emit({
            message: 'Failed to unassign table',
            variant: 'error',
          });
        },
      });
  }

  changeAssignmentFormSubmit(formValues: any): void {
    if (!formValues.valid) {
      this.toastMessage.emit({
        message: 'Please select an action',
        variant: 'alert',
      });
      return;
    }
    if (!this.checkedRows.length) {
      this.toastMessage.emit({
        message: 'No rows selected',
        variant: 'error',
      });
      return;
    }

    const action: string = formValues.formData.assignmentAction.value;
    const selectedTableIds: string[] = this.checkedRows
      .map((row) => row['tableId'])
      .filter((id): id is string => !!id);
    const existingIds = [...this.currentAssignedTableIds];

    let newIds: string[];
    if (action === 'assign') {
      newIds = computeAssignPayload(existingIds, selectedTableIds);
    } else {
      newIds = computeUnassignPayload(existingIds, selectedTableIds);
    }

    this.isLoading = true;

    let operation$: Observable<null>;
    if (newIds.length === 0) {
      operation$ = this.graphQLService
        .deleteAssignments(this.organizationId, this.authorizedParty)
        .pipe(
          map(() => {
            this.assignmentsExist = false;
            return null;
          })
        );
    } else if (!this.assignmentsExist) {
      operation$ = this.graphQLService
        .createAssignments(this.organizationId, this.authorizedParty, newIds)
        .pipe(
          map(() => {
            this.assignmentsExist = true;
            return null;
          })
        );
    } else {
      operation$ = this.graphQLService
        .updateAssignments(this.organizationId, this.authorizedParty, newIds)
        .pipe(
          map(() => {
            this.assignmentsExist = true;
            return null;
          })
        );
    }

    operation$
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeAssignmentDialog = false;
        })
      )
      .subscribe({
        next: () => {
          this.currentAssignedTableIds = new Set(newIds);
          const newAssignedSet = new Set(newIds);
          const updatedRows = this.apiAccessTableDataCopy.map((r) => {
            const isAssigned = newAssignedSet.has(r.tableId);
            return {
              ...r,
              assignedBadge: {
                isAssigned,
                color: isAssigned ? 'success-green-700' : 'error-red',
                label: isAssigned ? 'Yes' : 'No',
              },
            };
          });
          this.apiAccessTableData.set(updatedRows);
          this.apiAccessTableDataCopy = updatedRows;
          this.toastMessage.emit({
            message: 'Successfully changed table assignments',
            variant: 'success',
          });
        },
        error: () => {
          this.toastMessage.emit({
            message: 'Failed to change table assignments',
            variant: 'error',
          });
        },
      });
  }

  dialogFormCloseHandler(): void {
    this.openChangeAssignmentDialog = false;
  }
}
